import {ProtocolQuery} from '../service-model/protocol-query.interface';
import {ProtocolCommand} from '../service-model/protocol-command.interface';
import {SHELL_PROTOCOL_OPERATIONS_NS} from './operations/shell-protocol-namespace.const';

// CR-20260922-001 — the Action Board wire contract, mirroring
// ETS.ShellService.Shell.Protocol/Operations/ActionBoard/ActionBoardOperations.cs and
// Dto/ActionBoard/ActionBoardWireDtos.cs.
//
// Commands and queries go through the shell router as plain JSON.stringify (NO class-transformer
// decorators — see apg-orion-operations.ts); the backend deserializes case-insensitively into the
// PascalCase C# members. Ids travel as strings. Day labels are "yyyy-MM-dd" *DayKey strings; instants
// are ISO-8601 UTC strings named *AtUtc, which the reply reviver does NOT turn into Dates because the
// key contains neither "date" nor "time" — the panel formats them in the client's zone.

/** ESS message type of the "board changed" push (backend class short name). */
export const ACTION_BOARD_CHANGED_MESSAGE_TYPE = 'ActionBoardChangedDto';

export type ActionSeverity = 'red' | 'orange' | 'yellow' | 'green';

/** CR-20260924-001 F8 (owner 2026-09-24): no 'superseded' — a re-reported finding overwrites its item in place, it never closes it. */
export type ActionCompletionReason = 'satisfied' | 'acknowledged' | 'stale' | 'withdrawn';

export interface ActionItemWireDto {
    actionItemId: string;
    key: string;
    itemType: string;
    recipientUserId: string;
    source: string;
    sourceLabel: string;
    senderUserId: string | null;
    noticeId: string | null;
    subjectPath: string;
    subjectLabel: string;
    severity: ActionSeverity;
    headline: string;
    detail: string;
    payload: string | null;
    originatedAtUtc: string;
    originatedDayKey: string;
    dueDayKey: string;
    deliveredAtUtc: string | null;
    readAtUtc: string | null;
    lastEvaluatedAtUtc: string | null;
    isStale: boolean;
    completedAtUtc: string | null;
    completionReason: ActionCompletionReason | null;
    completedDayKey: string | null;
    cardAction: 'open-source-panel' | 'acknowledge' | 'none';
    openPanel: 'APG' | 'HedgingGrid' | 'Collars' | '';
}

export interface ActionItemEventWireDto {
    actionItemId: string;
    atUtc: string;
    kind: string;
    actor: string;
    evidence: string | null;
    note: string | null;
}

export interface ActionBoardUserWireDto {
    userId: string;
    userName: string;
    email: string;
    managerUserId: string | null;
}

export interface ActionBoardSubjectWireDto {
    path: string;
    label: string;
    /** Archive portfolios: the client the portfolio belongs to; null for Global (CR-20260929-002). */
    ownerUserId?: string | null;
}

export interface GetActionBoardReply {
    viewedUserId: string;
    isOwnBoard: boolean;
    todayDayKey: string;
    items: ActionItemWireDto[];
}

/** The archive's sort attributes (owner 2026-09-23, second screencast); applied on the server across pages. */
export type ArchiveSortKey = 'completed' | 'originated' | 'severity';

/** One link of the sort chain (owner 2026-09-23: "cumulative, not exclusive"): priority = position. */
export interface ArchiveSortLink {
    key: ArchiveSortKey;
    descending: boolean;
}

/** The Archive face (owner screencast 2026-09-23): one page of every completed item, server-filtered. */
export interface GetActionBoardArchiveReply {
    viewedUserId: string;
    isOwnBoard: boolean;
    todayDayKey: string;
    items: ActionItemWireDto[];
    /** Items passing the filter over every page. */
    totalCount: number;
    /** The page served: the requested one, clamped to the last page. */
    pageIndex: number;
    pageSize: number;
    /** Portfolios of the viewed user's completed items under the chosen source: path = key, label = display. */
    portfolios: ActionBoardSubjectWireDto[];
}

export interface ActionBoardChangedDto {
    topic: string;
    userId: string;
    outstandingCount: number;
    overdueCount: number;
    highestSeverity: ActionSeverity | '';
    changedAtUtc: string;
}

/** The client switch's "All" value and the viewedUserId of an All reply (CR-20260929-002). */
export const ALL_USERS_KEY = 'all';

export class GetActionBoard implements ProtocolQuery {
    /** allUsers = the "All" client switch (CR-20260929-002): own + every subordinate's items, resolved on the server. */
    constructor(public readonly viewedUserId: string | null, public readonly allUsers: boolean = false) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetActionBoard`;
    }
}

/**
 * The archive query. Every filter runs on the server (owner ruling 2026-09-23); empty/null = all.
 * Day keys bound the COMPLETION day, inclusive. Page index is 0-based; the page size is what the
 * panel measured to fit its viewport.
 */
export class GetActionBoardArchive implements ProtocolQuery {
    constructor(
        public readonly viewedUserId: string | null,
        public readonly fromDayKey: string | null,
        public readonly toDayKey: string | null,
        public readonly severities: ActionSeverity[],
        public readonly source: string,
        public readonly portfolio: string,
        public readonly search: string,
        public readonly sort: ArchiveSortLink[],
        public readonly pageIndex: number,
        public readonly pageSize: number,
        /** The "All" client switch (CR-20260929-002). */
        public readonly allUsers: boolean = false
    ) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetActionBoardArchive`;
    }
}

export class GetActionItemRecord implements ProtocolQuery {
    constructor(public readonly actionItemId: string) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetActionItemRecord`;
    }
}

export class GetActionBoardSubordinates implements ProtocolQuery {
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetActionBoardSubordinates`;
    }
}

export class GetActionBoardSubjects implements ProtocolQuery {
    constructor(public readonly userId: string | null) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetActionBoardSubjects`;
    }
}

export class GetOutstandingActionItemsByPath implements ProtocolQuery {
    constructor(public readonly userId: string | null, public readonly pathPrefix: string) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetOutstandingActionItemsByPath`;
    }
}

export class MarkActionItemsDelivered implements ProtocolCommand {
    constructor(public readonly actionItemIds: string[]) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.MarkActionItemsDelivered`;
    }
}

export class MarkActionItemRead implements ProtocolCommand {
    constructor(public readonly actionItemId: string) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.MarkActionItemRead`;
    }
}

/** Super user only, debugging (owner 2026-09-22): put one of the caller's own items back to unread. */
export class MarkActionItemUnread implements ProtocolCommand {
    constructor(public readonly actionItemId: string) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.MarkActionItemUnread`;
    }
}

export class AcknowledgeActionItem implements ProtocolCommand {
    /** evidence: how it was acknowledged, for the record ("opened in HedgingGrid"); omitted for the Acknowledge button. */
    constructor(public readonly actionItemId: string, public readonly evidence: string | null = null) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.AcknowledgeActionItem`;
    }
}

export class PostActionNotice implements ProtocolCommand {
    constructor(
        public readonly audience: 'user' | 'all',
        public readonly audienceUserId: string | null,
        public readonly subjectPath: string,
        public readonly subjectLabel: string,
        public readonly severity: ActionSeverity,
        public readonly headline: string,
        public readonly body: string,
        public readonly dueDayKey: string | null,
        /** Scheduled send (owner 2026-09-23): ISO-8601 UTC, minute precision; null = send now. */
        public readonly sendAtUtc: string | null = null
    ) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.PostActionNotice`;
    }
}

/** One alert waiting to be sent (owner 2026-09-23) — the composer's pending list. */
export interface ScheduledNoticeWireDto {
    scheduledNoticeId: string;
    audience: 'user' | 'all';
    audienceUserId: string | null;
    subjectPath: string;
    subjectLabel: string;
    severity: ActionSeverity;
    headline: string;
    body: string;
    dueDayKey: string | null;
    /** ISO-8601 UTC, minute precision. */
    sendAtUtc: string;
    createdAtUtc: string;
}

/** The caller's own pending scheduled alerts, soonest first. */
export class GetScheduledActionNotices implements ProtocolQuery {
    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.GetScheduledActionNotices`;
    }
}

/** Cancel one pending scheduled alert of the caller (cancel only, never edit — owner 2026-09-23). */
export class CancelScheduledActionNotice implements ProtocolCommand {
    constructor(public readonly scheduledNoticeId: string) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.CancelScheduledActionNotice`;
    }
}

export class WithdrawActionNotice implements ProtocolCommand {
    constructor(public readonly noticeId: string) {
    }

    getOperationName(): string {
        return `${SHELL_PROTOCOL_OPERATIONS_NS}.WithdrawActionNotice`;
    }
}
